import { fetchAPI } from './api';

export interface SubscriptionData {
    is_active: boolean;
    lemonsqueezy_subscription_id?: string;
    subscription_status?: string;
    last_updated_utc?: string;
    renews_at?: string;
    ends_at?: string;
    variant_name?: 'pro' | 'team' | null;
    is_sub_owner?: boolean;
}

export interface Organization {
    org_id: string;
    name: string;
}

export interface OrganizationUser {
    user_id: string;
    admin: boolean;
    nickname?: string;
    email?: string;
}

export interface MyOrgData {
    organization: Organization | null;
    admin: boolean;
}

export async function hasActiveSubscription(): Promise<boolean> {
    try {
        const subscription = await getSubscription();
        return subscription.is_active;
    } catch (error) {
        return false;
    }
}

export async function getSubscription(): Promise<SubscriptionData> {
    try {
        const response = await fetchAPI('/subscription', { method: 'GET' });
        return response;
    } catch (error) {
        console.error('Error getting subscription:', error);
        throw error;
    }
}

export async function getMyOrg(): Promise<MyOrgData> {
    try {
        const response = await fetchAPI('/my-org', { method: 'GET' });
        return response;
    } catch (error) {
        console.error('Error getting my organization:', error);
        throw error;
    }
}

export async function getOrgUsers(orgId: string): Promise<OrganizationUser[]> {
    try {
        const response = await fetchAPI(`/orgs/${orgId}/users`, { method: 'GET' });
        return response.users;
    } catch (error) {
        console.error('Error getting organization users:', error);
        throw error;
    }
}

export async function createOrg(name: string): Promise<{ org_id: string; name: string }> {
    try {
        const response = await fetchAPI('/orgs', { 
            method: 'POST',
            body: JSON.stringify({ name })
        });
        return response;
    } catch (error) {
        console.error('Error creating organization:', error);
        throw error;
    }
}

export async function getUserByEmail(email: string): Promise<{ user_id: string; email: string }> {
    try {
        const response = await fetchAPI(`/users/email?user_email=${encodeURIComponent(email)}`, { 
            method: 'GET'
        });
        return response;
    } catch (error: any) {
        console.error('Error getting user by email:', error);
        
        // Check if it's a 404 error (user not found)
        if (error.message && error.message.includes('404')) {
            throw new Error(`No user with email "${email}" found. The user needs to create a free account first.`);
        }
        
        // Re-throw other errors
        throw error;
    }
}

export async function addTeamMember(orgId: string, userId: string, isAdmin: boolean = false, nickname?: string): Promise<void> {
    try {
        const body: any = { user_id: userId, admin: isAdmin };
        if (nickname) {
            body.nickname = nickname;
        }
        
        await fetchAPI(`/orgs/${orgId}/users`, { 
            method: 'POST',
            body: JSON.stringify(body)
        });
    } catch (error) {
        console.error('Error adding team member:', error);
        throw error;
    }
}

export async function removeTeamMember(orgId: string, userId: string): Promise<void> {
    try {
        await fetchAPI(`/orgs/${orgId}/users/${userId}`, { 
            method: 'DELETE'
        });
    } catch (error) {
        console.error('Error removing team member:', error);
        throw error;
    }
}

export async function updateOrgName(orgId: string, name: string): Promise<void> {
    try {
        await fetchAPI(`/orgs/${orgId}`, { 
            method: 'PATCH',
            body: JSON.stringify({ name })
        });
    } catch (error) {
        console.error('Error updating organization name:', error);
        throw error;
    }
} 